<script setup lang="ts">
  import { computed, ref, onMounted, onUnmounted } from 'vue';
  import { useErrorHandlerStore } from '@/store';
  import axios, { AxiosError } from 'axios';
  import { useI18n } from 'vue-i18n';
  import { createClickOutsideListener } from '@/utils/clickOutside';
  import CrossIcon from '@/assets/cross_icon.svg';

  const errorHandlerStore = useErrorHandlerStore();
  const isAxios = computed(() => axios.isAxiosError(errorHandlerStore.error?.originalError));
  const { t } = useI18n();

  // Modal logic.
  const emit = defineEmits(['close']);
  const loading = ref(false);
  const clickListener = createClickOutsideListener(['#contact-modal'], () => {
    emit('close');
  });
  onMounted(async () => {
    setTimeout(() => {
      clickListener.start();
    }, 0);
  });
  onUnmounted(() => {
    clickListener.stop();
  });
</script>

<template>
  <div class="absolute inset-0 backdrop-blur-sm z-20 p-4">
    <div class="relative inset-0 h-full">
      <div
        id="error-modal"
        class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-full max-w-[700px] h-fit bg-white rounded-2xl p-6 flex flex-col items-center z-30 shadow-lg"
        v-rc-loader="{ loading }"
      >
        <header class="pt-2">
          <!-- title -->
          <h3 class="text-primary font-semibold truncate text-[1.125rem] text-center flex-1 mb-8">
            {{
              errorHandlerStore.error && errorHandlerStore.error.title
                ? $t(errorHandlerStore.error.title)
                : $t('error.warning')
            }}
          </h3>
          <button
            type="button"
            :aria-label="$t('modal.close')"
            class="flex justify-center items-center hover:bg-ternary rounded-full h-7 w-7 absolute top-2 right-2"
            @click="emit('close')"
          >
            <cross-icon class="stroke-grey-dark stroke-[5px] scale-[.33]" />
          </button>
        </header>

        <div class="text-center">
          <!-- description -->
          <p
            class="text-grey-darkest mb-4"
            v-if="errorHandlerStore.error && errorHandlerStore.error.detail"
          >
            {{ t(errorHandlerStore.error.detail) }}
          </p>
          <!-- description fallback -->
          <p
            class="text-grey-darkest mb-4"
            v-else
          >
            {{ t('error.default') }}
          </p>

          <!-- original error -->
          <section class="text-[0.875rem]">
            <p
              v-if="errorHandlerStore.error?.originalError"
              class="mb-4"
            >
              <!-- status code -->
              <span
                class="font-bold block"
                v-if="isAxios"
                >{{ (errorHandlerStore.error?.originalError as AxiosError).request.status }}</span
              >

              {{ errorHandlerStore.error?.originalError }}
            </p>
          </section>

          <!-- countdown to redirection -->
          <hr v-if="errorHandlerStore.countdown > 0" />
          <p
            v-if="errorHandlerStore.countdown > 0"
            :textContent="`${t('error.redirection')} ${errorHandlerStore.countdown}`"
            class="pt-0 pb-10 max-w-[600px] text-[21px]"
          ></p>
        </div>
      </div>
    </div>
  </div>
</template>
